import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdPeople, MdFolder, MdClose, MdHistory } from 'react-icons/md';
import { fetchWrapper } from '../../utils/fetchWrapper';
import './GlobalSearch.css';

const GlobalSearch = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const searchRef = useRef(null);
    const navigate = useNavigate();
    const abortControllerRef = useRef(null);

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ctrl + K to focus/open
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setIsOpen(true);
                setTimeout(() => document.getElementById('global-search-input')?.focus(), 10);
            }
            // Esc to close
            if (e.key === 'Escape') {
                setIsOpen(false);
                setQuery('');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Debounced Fetching with Cancellation
    useEffect(() => {
        if (!query || query.length < 2) {
            setResults([]);
            setIsLoading(false);
            return;
        }

        const timer = setTimeout(() => {
            performSearch(query);
        }, 300);

        return () => clearTimeout(timer);
    }, [query]);

    const performSearch = async (searchTerm) => {
        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        try {
            const res = await fetchWrapper.get(`/search?q=${encodeURIComponent(searchTerm)}`, {
                signal: controller.signal
            });
            
            if (res.data.success) {
                setResults(res.data.results || []);
                setActiveIndex(-1);
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Search failed:', err);
            }
        } finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    };

    const handleKeyDown = (e) => {
        if (results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => (prev > 0 ? prev - 1 : prev));
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            handleSelect(results[activeIndex]);
        }
    };

    const handleSelect = (item) => {
        navigate(item.route);
        setIsOpen(false);
        setQuery('');
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={`global-search-wrapper ${isOpen ? 'is-searching' : ''}`} ref={searchRef}>
            <div className="search-trigger" onClick={() => setIsOpen(true)}>
                <MdSearch size={18} />
                <span className="search-placeholder">Search... <kbd>Ctrl K</kbd></span>
            </div>

            {isOpen && (
                <div className="search-flyout card-base">
                    <div className="search-input-header">
                        <MdSearch size={20} className="text-muted" />
                        <input
                            id="global-search-input"
                            type="text"
                            placeholder="Find leads, projects..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            autoComplete="off"
                        />
                        {isLoading ? (
                            <div className="search-spinner" />
                        ) : (
                            <div className="search-close-hint" onClick={() => setIsOpen(false)}>
                                <MdClose size={18} />
                            </div>
                        )}
                    </div>

                    <div className="search-results-content">
                        {query.length > 0 && query.length < 2 && (
                            <div className="search-prompt">
                                <MdHistory className="me-2" />
                                <span>Type at least 2 characters to search</span>
                            </div>
                        )}

                        {results.length > 0 ? (
                            <div className="results-scroll">
                                {results.map((item, index) => (
                                    <div
                                        key={`${item.type}-${item.id}`}
                                        className={`search-result-item ${index === activeIndex ? 'active' : ''}`}
                                        onClick={() => handleSelect(item)}
                                        onMouseEnter={() => setActiveIndex(index)}
                                    >
                                        <div className={`result-icon ${item.type}`}>
                                            {item.type === 'lead' ? <MdPeople /> : <MdFolder />}
                                        </div>
                                        <div className="result-info">
                                            <div className="result-title">
                                                {item.title}
                                                <span className={`result-badge ${item.type}`}>{item.type}</span>
                                            </div>
                                            <div className="result-subtitle">{item.subtitle}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : query.length >= 2 && !isLoading ? (
                            <div className="search-empty">
                                <p>No results found for "<strong>{query}</strong>"</p>
                                <span>Try searching by phone number or domain name</span>
                            </div>
                        ) : null}
                    </div>

                    <div className="search-footer">
                        <span>Select with <kbd>Enter</kbd></span>
                        <span>Navigate with <kbd>↑↓</kbd></span>
                        <span>Close with <kbd>Esc</kbd></span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalSearch;
